import React, { useMemo, useState } from 'react';
import { Search, Filter, ArrowUpDown, ArrowUp, ArrowDown, Download } from 'lucide-react';
import { transactions } from '../../../data/mockData';
import type { Transaction } from '../../../data/mockData';
const categories = ['All', 'Food & Dining', 'Transport', 'Shopping', 'Entertainment', 'Bills & Utilities', 'Healthcare', 'Travel', 'Income'];
const categoryColors: Record<string, string> = {
  'Food & Dining': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  Transport: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Shopping: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
  Entertainment: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  'Bills & Utilities': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  Healthcare: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Travel: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
  Income: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
};
export function TransactionsView() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortField, setSortField] = useState<'date' | 'amount'>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const perPage = 10;
  const filtered = useMemo(() => {
    let result = [...transactions];
    if (search) result = result.filter(t => t.merchant.toLowerCase().includes(search.toLowerCase()) || t.category.toLowerCase().includes(search.toLowerCase()));
    if (selectedCategory !== 'All') result = result.filter(t => t.category === selectedCategory);
    result.sort((a, b) => {
      if (sortField === 'date') return sortDir === 'desc' ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date);
      return sortDir === 'desc' ? Math.abs(b.amount) - Math.abs(a.amount) : Math.abs(a.amount) - Math.abs(b.amount);
    });
    return result;
  }, [search, selectedCategory, sortField, sortDir]);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);
  const totalIn = filtered.filter(t => t.type === 'credit').reduce((s, t) => s + t.amount, 0);
  const totalOut = filtered.filter(t => t.type === 'debit').reduce((s, t) => s + Math.abs(t.amount), 0);
  const toggleSort = (field: 'date' | 'amount') => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  return <div className="space-y-5" data-id="element-740">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4" data-id="element-741">
        {[{
        label: 'Total In',
        value: `+$${totalIn.toLocaleString('en', {
          minimumFractionDigits: 2
        })}`,
        color: 'text-accent-600 dark:text-accent-400',
        bg: 'bg-accent-50 dark:bg-accent-900/20'
      }, {
        label: 'Total Out',
        value: `-$${totalOut.toLocaleString('en', {
          minimumFractionDigits: 2
        })}`,
        color: 'text-red-600 dark:text-red-400',
        bg: 'bg-red-50 dark:bg-red-900/20'
      }, {
        label: 'Net',
        value: `${totalIn - totalOut >= 0 ? '+' : ''}$${(totalIn - totalOut).toLocaleString('en', {
          minimumFractionDigits: 2
        })}`,
        color: totalIn - totalOut >= 0 ? 'text-accent-600 dark:text-accent-400' : 'text-red-600',
        bg: 'bg-gray-50 dark:bg-dark-hover'
      }].map(s => <div key={s.label} className={`${s.bg} rounded-2xl p-4 border border-gray-100 dark:border-dark-border`} data-id="element-742">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-743">
              {s.label}
            </p>
            <p className={`text-xl font-bold ${s.color}`} data-id="element-744">{s.value}</p>
          </div>)}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-dark-card rounded-2xl p-4 border border-gray-100 dark:border-dark-border shadow-sm" data-id="element-745">
        <div className="flex flex-wrap gap-3 items-center" data-id="element-746">
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-dark-hover rounded-xl px-3 py-2 flex-1 min-w-48" data-id="element-747">
            <Search size={15} className="text-gray-400 shrink-0" data-id="element-748" />
            <input type="text" placeholder="Search transactions..." value={search} onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }} className="bg-transparent text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none w-full" data-id="element-749" />
          </div>
          <div className="flex gap-2 flex-wrap" data-id="element-750">
            {categories.map(cat => <button key={cat} onClick={() => {
            setSelectedCategory(cat);
            setPage(1);
          }} className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${selectedCategory === cat ? 'bg-primary-500 text-white' : 'bg-gray-100 dark:bg-dark-hover text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-border'}`} data-id="element-751">
                {cat}
              </button>)}
          </div>
          <button className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-dark-hover px-3 py-2 rounded-xl hover:bg-gray-200 dark:hover:bg-dark-border transition-colors ml-auto" data-id="element-752">
            <Download size={14} data-id="element-753" /> Export
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-card rounded-2xl border border-gray-100 dark:border-dark-border shadow-sm overflow-hidden" data-id="element-754">
        <table className="w-full" data-id="element-755">
          <thead data-id="element-756">
            <tr className="border-b border-gray-100 dark:border-dark-border" data-id="element-757">
              <th className="text-left px-5 py-3" data-id="element-758">
                <button onClick={() => toggleSort('date')} className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200" data-id="element-759">
                  Date{' '}
                  {sortField === 'date' ? sortDir === 'desc' ? <ArrowDown size={11} data-id="element-760" /> : <ArrowUp size={11} data-id="element-761" /> : <ArrowUpDown size={11} data-id="element-762" />}
                </button>
              </th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide" data-id="element-763">
                Merchant
              </th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide" data-id="element-764">
                Category
              </th>
              <th className="text-right px-5 py-3" data-id="element-765">
                <button onClick={() => toggleSort('amount')} className="flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide hover:text-gray-700 dark:hover:text-gray-200 ml-auto" data-id="element-766">
                  Amount{' '}
                  {sortField === 'amount' ? sortDir === 'desc' ? <ArrowDown size={11} data-id="element-767" /> : <ArrowUp size={11} data-id="element-768" /> : <ArrowUpDown size={11} data-id="element-769" />}
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 dark:divide-dark-border" data-id="element-770">
            {paginated.map(tx => <tr key={tx.id} className="hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors" data-id="element-771">
                <td className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap" data-id="element-772">
                  {tx.date}
                </td>
                <td className="px-5 py-3" data-id="element-773">
                  <div className="flex items-center gap-3" data-id="element-774">
                    <span className="text-xl" data-id="element-775">{tx.icon}</span>
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200" data-id="element-776">
                      {tx.merchant}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3" data-id="element-777">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${categoryColors[tx.category] || 'bg-gray-100 text-gray-600'}`} data-id="element-778">
                    {tx.category}
                  </span>
                </td>
                <td className={`px-5 py-3 text-sm font-semibold text-right whitespace-nowrap ${tx.type === 'credit' ? 'text-accent-600 dark:text-accent-400' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-779">
                  {tx.type === 'credit' ? '+' : '-'}$
                  {Math.abs(tx.amount).toFixed(2)}
                </td>
              </tr>)}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 dark:border-dark-border" data-id="element-780">
          <p className="text-xs text-gray-400" data-id="element-781">
            {filtered.length} transactions
          </p>
          <div className="flex items-center gap-2" data-id="element-782">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-dark-hover text-gray-600 dark:text-gray-400 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-dark-border transition-colors" data-id="element-783">
              Previous
            </button>
            <span className="text-xs text-gray-500" data-id="element-784">
              {page} / {totalPages}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-dark-hover text-gray-600 dark:text-gray-400 disabled:opacity-40 hover:bg-gray-200 dark:hover:bg-dark-border transition-colors" data-id="element-785">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}